import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Activity, CheckCircle2, XCircle, Clock, AlertTriangle,
  TrendingUp, RefreshCw, Bell, Play, Pause, Calendar
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ActivityJournal from '@/components/ActivityJournal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Monitoring = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [statsByAutomation, setStatsByAutomation] = useState([]);
  const [statsByDay, setStatsByDay] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const DAYS = 30;  // window for the overview stats/charts


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, byAutoRes, byDayRes, alertsRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/monitoring/stats`),
        axios.get(`${API}/monitoring/stats/by-automation`),
        axios.get(`${API}/monitoring/stats/by-day?days=${DAYS}`),
        axios.get(`${API}/monitoring/alerts?days=${DAYS}`),
        axios.get(`${API}/execution/schedule/status`)
      ]);

      setStats(statsRes.data);
      setStatsByAutomation(byAutoRes.data);
      setStatsByDay(byDayRes.data);
      setAlerts(alertsRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sendTestAlert = async () => {
    try {
      await axios.post(`${API}/monitoring/test-alert`, {});
      toast.success('Alerte de test envoyée au super admin');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'alerte test');
    }
  };

  const toggleScheduler = async (start) => {
    try {
      if (start) {
        await axios.post(`${API}/execution/schedule/start`, {});
        toast.success('Scheduler démarré !');
      } else {
        await axios.post(`${API}/execution/schedule/stop`, {});
        toast.success('Scheduler arrêté');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du contrôle du scheduler');
    }
  };

  const runSchedulerNow = async () => {
    try {
      await axios.post(`${API}/execution/schedule/run-now`, {});
      toast.success('Exécution du scheduler lancée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'exécution');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-foreground">
            Monitoring
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed text-sm sm:text-base">
            Suivez l'exécution de vos automations en temps réel
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="rounded-full"
          >
            <RefreshCw size={16} className="mr-2" />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={sendTestAlert}
            className="rounded-full"
            data-testid="test-alert-btn"
          >
            <Bell size={16} className="mr-2" />
            Test Alerte
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Exécutions</p>
                <p className="text-2xl font-bold">{stats?.total_executions || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Réussies</p>
                <p className="text-2xl font-bold text-green-500">{stats?.successful || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Échouées</p>
                <p className="text-2xl font-bold text-red-500">{stats?.failed || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taux de Succès</p>
                <p className="text-2xl font-bold">{stats?.success_rate || 0}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last 24h Alert */}
      {(stats?.last_24h_failures || 0) > 0 && (
        <Card className="bg-red-500/10 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="font-medium text-red-800">
                  {stats.last_24h_failures} échec(s) dans les dernières 24h
                </p>
                <p className="text-sm text-red-700">
                  Sur {stats.last_24h_executions} exécutions totales
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="dashboard" data-testid="tab-monitoring-dashboard">
            <TrendingUp size={16} className="mr-2" />
            <span className="hidden sm:inline">Vue d'ensemble</span>
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">
            <Activity size={16} className="mr-2" />
            <span className="hidden sm:inline">Journal d'activité</span>
          </TabsTrigger>
          <TabsTrigger value="scheduler" data-testid="tab-scheduler">
            <Calendar size={16} className="mr-2" />
            <span className="hidden sm:inline">Scheduler</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <Bell size={16} className="mr-2" />
            <span className="hidden sm:inline">Alertes</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {/* Stats by Automation */}
          <Card>
            <CardHeader>
              <CardTitle>Performance par Automation</CardTitle>
              <CardDescription>Statistiques de chaque automation</CardDescription>
            </CardHeader>
            <CardContent>
              {statsByAutomation.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-3">
                  {statsByAutomation.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.automation_name || 'Sans nom'}</p>
                        <p className="text-sm text-muted-foreground">
                          Dernière exécution: {formatDate(item.last_execution)}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-500">{item.successful} ✓</span>
                        <span className="text-red-500">{item.failed} ✗</span>
                        <Badge variant={item.success_rate >= 90 ? "default" : item.success_rate >= 70 ? "secondary" : "destructive"}>
                          {item.success_rate}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats by Day */}
          <Card>
            <CardHeader>
              <CardTitle>Activité des 7 derniers jours</CardTitle>
            </CardHeader>
            <CardContent>
              {statsByDay.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-2">
                  {statsByDay.map((day, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="w-24 text-sm text-muted-foreground">{day.date}</span>
                      <div className="flex-1 flex items-center gap-2">
                        <div 
                          className="h-6 bg-green-500 rounded"
                          style={{ width: `${Math.max(day.successful * 5, 4)}px` }}
                        ></div>
                        <div 
                          className="h-6 bg-red-500 rounded"
                          style={{ width: `${Math.max(day.failed * 5, day.failed > 0 ? 4 : 0)}px` }}
                        ></div>
                        <span className="text-sm text-muted-foreground ml-2">
                          {day.total} total ({day.successful} ✓, {day.failed} ✗)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="journal" className="mt-6">
          <ActivityJournal />
        </TabsContent>

        <TabsContent value="scheduler" className="mt-6 space-y-6">
          {/* Scheduler Status Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar size={20} />
                    Scheduler Automatique
                  </CardTitle>
                  <CardDescription>
                    Exécution automatique des automations temporelles
                  </CardDescription>
                </div>
                <Badge variant={schedulerStatus?.scheduler?.running ? "default" : "secondary"} className="text-sm">
                  {schedulerStatus?.scheduler?.running ? (
                    <><CheckCircle2 size={14} className="mr-1" /> Actif</>
                  ) : (
                    <><XCircle size={14} className="mr-1" /> Inactif</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {schedulerStatus?.scheduler?.running ? (
                  <Button
                    variant="outline"
                    onClick={() => toggleScheduler(false)}
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    data-testid="stop-scheduler-btn"
                  >
                    <Pause size={16} className="mr-2" />
                    Arrêter le Scheduler
                  </Button>
                ) : (
                  <Button
                    onClick={() => toggleScheduler(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="start-scheduler-btn"
                  >
                    <Play size={16} className="mr-2" />
                    Démarrer le Scheduler
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={runSchedulerNow}
                  data-testid="run-now-btn"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Exécuter maintenant
                </Button>
              </div>

              {schedulerStatus?.scheduler?.jobs?.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Jobs planifiés</h4>
                  <div className="space-y-2">
                    {schedulerStatus.scheduler.jobs.map((job, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="font-medium">{job.name}</span>
                        <span className="text-muted-foreground">
                          Prochaine exécution: {job.next_run ? new Date(job.next_run).toLocaleString('fr-FR') : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {schedulerStatus?.time_based_automations || 0}
                    </p>
                    <p className="text-sm text-blue-700">Automations temporelles</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {schedulerStatus?.recent_runs?.length || 0}
                    </p>
                    <p className="text-sm text-purple-700">Exécutions récentes</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Recent Scheduler Runs */}
          {schedulerStatus?.recent_runs?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique des exécutions du Scheduler</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {schedulerStatus.recent_runs.map((run, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">Exécution automatique</p>
                        <p className="text-sm text-muted-foreground">
                          {run.automations_checked} automations vérifiées, {run.clients_matched} clients traités
                        </p>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(run.executed_at).toLocaleString('fr-FR')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Comment fonctionne le Scheduler ?</h4>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Exécution automatique quotidienne à 9h00</li>
                    <li>Vérification toutes les heures des triggers temporels</li>
                    <li>Seules les automations en mode "Temporel" sont exécutées</li>
                    <li>Les résultats sont visibles dans l'onglet "Exécutions"</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Alertes Envoyées</CardTitle>
              <CardDescription>
                Notifications envoyées au super admin (michael@pkaila.com)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune alerte envoyée</p>
                  <p className="text-sm text-gray-400">Tout fonctionne correctement !</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-red-800">
                              {alert.service?.toUpperCase()} - {alert.alert_type}
                            </p>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(alert.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-red-700 mt-1">{alert.error_message}</p>
                          {alert.automation_name && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Automation: {alert.automation_name}
                            </p>
                          )}
                          {alert.client_email && (
                            <p className="text-sm text-muted-foreground">
                              Client: {alert.client_email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Monitoring;
